'use client'

import { createContext, useContext, useEffect, useMemo } from 'react'
import type { PostHogInterface } from 'posthog-js'
import posthog from 'posthog-js'
import { registerAttributionProperties } from '@/lib/analytics/posthog'

const PostHogContext = createContext<PostHogInterface | null>(null)

let isInitialized = false
let hasLoggedMissingConfig = false
let hasLoggedLocalhostDisable = false

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const client = useMemo(() => posthog as PostHogInterface, [])

  useEffect(() => {
    if (isInitialized) {
      return
    }

    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST
    const allowInDev = process.env.NEXT_PUBLIC_POSTHOG_DEV === 'true'

    if (!apiKey || !apiHost) {
      if (
        process.env.NODE_ENV === 'development' &&
        !hasLoggedMissingConfig
      ) {
        hasLoggedMissingConfig = true
        console.warn(
          'PostHog is disabled because NEXT_PUBLIC_POSTHOG_KEY or NEXT_PUBLIC_POSTHOG_HOST is missing.'
        )
      }
      return
    }

    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1')

    // Avoid noisy network failures in local development unless explicitly enabled.
    if (process.env.NODE_ENV !== 'production' && isLocalhost && !allowInDev) {
      if (!hasLoggedLocalhostDisable) {
        hasLoggedLocalhostDisable = true
        console.info(
          'PostHog is disabled on localhost. Set NEXT_PUBLIC_POSTHOG_DEV=true to enable analytics during local development.'
        )
      }
      return
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = String(event.reason ?? '')
      const stack = String((event.reason as { stack?: string })?.stack ?? '')

      if (
        reason.includes('Failed to fetch') &&
        (reason.includes('posthog') || stack.includes('posthog-js'))
      ) {
        event.preventDefault()
      }
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    try {
      posthog.init(apiKey, {
        api_host: apiHost,
        defaults: '2025-11-30',
        capture_pageview: 'history_change',
        // These add event listeners and extra client work on every route.
        // We rely on explicit events for the key actions we care about.
        capture_pageleave: false,
        autocapture: false,
        loaded: (loadedPostHog) => {
          registerAttributionProperties(loadedPostHog)

          if (process.env.NODE_ENV === 'development') {
            loadedPostHog.debug()
          }
        },
      })
    } catch {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      return
    }

    isInitialized = true

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  return (
    <PostHogContext.Provider value={client}>
      {children}
    </PostHogContext.Provider>
  )
}

export function usePostHog() {
  return useContext(PostHogContext)
}
